import { Controller } from '@hotwired/stimulus';

// Click-to-expand goal row on the Goals v3 page (both tiers).
//
// Mounted on the goal's card. A click anywhere on the collapsed row (`row`
// target) toggles the `detail` target, a Bootstrap collapse holding the full
// goal text and the panels. The caret + coordinate inside the row is a real
// <button> (`disclosure` target): it carries aria-expanded for assistive
// technology and gives keyboard users Enter/Space through the button's
// native click, which bubbles to the row like any other click. Nested
// controls (the actions kebab, its dropdown items and modals, the story
// link) sit under a `[data-goals-v3-row-ignore]` marker and never toggle
// the row.
//
// Open state is mirrored two ways: aria-expanded on the disclosure button
// and an `is-open` class on the row (the caret rotation and the story
// strip's corner radius key on it). Both flip immediately on a toggle so
// the row acknowledges the click before Bootstrap's height animation ends;
// the plugin's shown/hidden events (jQuery-triggered, so bound with jQuery)
// then reconcile the final state. Nested panel collapses bubble the same
// events up through the detail, so only events whose target IS the detail
// count. Without jQuery/Bootstrap (unit tests) the controller falls back to
// toggling the `show` class directly.
//
// Targets:
//   row        - the clickable row (gets `is-open`)
//   disclosure - the caret/coordinate button (carries aria-expanded)
//   detail     - the collapsible detail block
//   panel      - the collapsible panels inside the detail (data-panel="details" ...)
export default class extends Controller {
  static targets = ['row', 'disclosure', 'detail', 'panel'];

  connect() {
    this.onCollapseEvent = (event) => {
      if (event.target !== this.detailTarget) return;
      this.syncExpanded(event.type === 'shown');
    };
    if (this.hasDetailTarget && this.jquery()) {
      this.jquery()(this.detailTarget)
        .on('shown.bs.collapse', this.onCollapseEvent)
        .on('hidden.bs.collapse', this.onCollapseEvent);
    }
  }

  disconnect() {
    if (this.hasDetailTarget && this.jquery()) {
      this.jquery()(this.detailTarget)
        .off('shown.bs.collapse', this.onCollapseEvent)
        .off('hidden.bs.collapse', this.onCollapseEvent);
    }
  }

  // Row click (including the disclosure button's native activation).
  // Anything under an ignore marker is somebody else's click.
  toggle(event) {
    if (this.ignored(event.target)) return;
    this.setOpen(!this.isOpen);
  }

  // Master toggle entry points. `open(panelKey)` expands the row and shows
  // exactly that panel (a row without that panel just expands); `close()`
  // hides every panel and collapses the row. Both apply state synchronously
  // (no animation) so a page-wide sweep can never be dropped mid-transition.
  open(panelKey) {
    this.applyOpen(true);
    this.panelTargets.forEach((panel) => {
      this.applyPanel(panel, panel.dataset.panel === panelKey);
    });
  }

  close() {
    this.panelTargets.forEach((panel) => this.applyPanel(panel, false));
    this.applyOpen(false);
  }

  get isOpen() {
    return this.rowTarget.classList.contains('is-open');
  }

  // Animated per-row toggle through the plugin when it exists.
  setOpen(open) {
    if (!this.hasDetailTarget) return;
    const $ = this.jquery();
    if ($ && $.fn && $.fn.collapse) {
      if (this.detailTarget.classList.contains('collapsing')) return;
      this.syncExpanded(open);
      $(this.detailTarget).collapse(open ? 'show' : 'hide');
      return;
    }
    this.applyOpen(open);
  }

  // Direct state application: the plugin reads `show` from the DOM at call
  // time, so later per-row plugin toggles stay consistent with this.
  applyOpen(open) {
    if (!this.hasDetailTarget) return;
    this.detailTarget.classList.remove('collapsing');
    this.detailTarget.classList.add('collapse');
    this.detailTarget.classList.toggle('show', open);
    this.detailTarget.style.height = '';
    this.syncExpanded(open);
  }

  applyPanel(panel, open) {
    panel.classList.remove('collapsing');
    panel.classList.add('collapse');
    panel.classList.toggle('show', open);
    panel.style.height = '';
    if (panel.id) {
      this.element
        .querySelectorAll(
          `[data-toggle="collapse"][href="#${panel.id}"], [data-toggle="collapse"][data-target="#${panel.id}"]`
        )
        .forEach((pill) => {
          pill.setAttribute('aria-expanded', open ? 'true' : 'false');
          pill.classList.toggle('collapsed', !open);
        });
    }
  }

  syncExpanded(open) {
    if (this.hasRowTarget) this.rowTarget.classList.toggle('is-open', open);
    if (this.hasDisclosureTarget) this.disclosureTarget.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  ignored(target) {
    return Boolean(target && target.closest && target.closest('[data-goals-v3-row-ignore]'));
  }

  jquery() {
    return typeof window !== 'undefined' && typeof window.$ === 'function' ? window.$ : null;
  }
}
